<script>
import useSharedData from "@/use/SharedData";
import useTools from "@/use/Tools";
import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation";
import useSharedMethod from "@/use/SharedMethod";
import {useOutStateModelStore} from "@/stores/OutStateModel";
import useSharedSelectData from "@/use/SharedSelectData";
import axios from "axios";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);

        const outStateModelStore = useOutStateModelStore();

        const {getBack} = useSharedMethod();
        const {mxRules} = useValidation();
        const {$refs, $route} = useTools();
        const {loading, dialog, overlay} = useSharedData();
        const {outStateModelFiles} = useSharedSelectData();

        const name = ref(null);
        const type = ref(null);
        const file = ref(null);

        const outStateModel = ref({});

        function findById() {
            loading.value = true;
            axios.get(`/outStateModels/${$route.params.id}`).then((response) => {
                outStateModel.value = response.data;
                name.value = outStateModel.value.name;
                type.value = outStateModel.value.type;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                const formData = new FormData();
                formData.append("file", file.value);
                axios.post(`/outStateModels`, formData, {
                    params: {
                        name: name.value,
                        type: type.value,
                    },
                }).then((response) => {
                    outStateModelStore.addOutStateModel(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                const formData = new FormData();
                formData.append("file", file.value);
                axios.put(`/outStateModels`, formData, {
                    params: {
                        id: $route.params.id,
                        name: name.value,
                        type: type.value,
                    },
                }).then((response) => {
                    outStateModelStore.updateOutStateModel(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/outStateModels/${outStateModel.value.id}`).then(() => {
                outStateModelStore.deleteOutStateModel(outStateModel.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if(!props.addAction)
                findById();
        });

        return {
            mxRules,
            readonly,
            loading,
            dialog,
            overlay,
            name,
            type,
            file,
            outStateModelFiles,
            getBack,
            save,
            update,
            remove
        }

    }
};
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        max-width="800"
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            />
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            />
            <v-card-text>
                <v-form lazy-validation ref="form">
                    <v-container fluid>
                        <w-fieldset :title="$t('common.details')">
                            <v-row>
                                <v-col cols="12">
                                    <w-label :label="$t('common.label')" required/>
                                    <w-text-field
                                        v-model="name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.type')" required/>
                                    <w-enum-select
                                        v-model="type"
                                        :items="outStateModelFiles"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="!readonly">
                                    <w-label :label="$t('common.file')" :required="addAction"/>
                                    <w-file-input
                                        v-model="file"
                                        :rules="addAction ? [mxRules.required]: []"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>